import HeaderSection from '@/components/Header/HeaderSection.vue';
import PortfolioSuperSetChart from '@/components/PortfolioSuperSetChart/PortfolioSuperSetChart.vue';
import PortfolioSidebar from '@/components/PortfolioSidebar/PortfolioSidebar.vue';
import BolideAssistant from '@/components/BolideAssistant/BolideAssistant.vue';
import HelpSupport from '@/components/helpSupport/HelpSupport.vue';
import ProgrammeComponent from '@/components/Programme/ProgrammeComponent.vue';
import SettingsComponent from '@/components/Settings/SettingsComponent.vue';
import VerificationComponent from '@/components/Verification/VerificationComponent.vue';
import IntegrationComponent from '@/components/Integration/IntegrationComponent.vue';
import { embedDashboard } from '@superset-ui/embedded-sdk';
import { get_token } from '@/router';
import getTilesConfiguration from '@/data/tiles';
import {
  global_comparisons_dashboard_id
} from '@/config/dashboardIds';
import BolideLoader from '@/components/BolideLoader/BolideLoader.vue';
import CompanyProfile from '@/components/CompanyProfile/CompanyProfile.vue';
import CertificatesAndReports from '@/components/CertificatesAndReports/CertificatesAndReports.vue';

const superset_base_domain = process.env.VUE_APP_SUPERSET_BASE_URL;
const guest_token_url = process.env.VUE_APP_GUEST_TOKEN_URL;

const SUPERSET_CONFIG = {
  baseUrl: superset_base_domain,
  defaultDashboardIds: {
    main: global_comparisons_dashboard_id
  }
};

const AUTH_CONFIG = {
  guestTokenUrl: guest_token_url
};

const TOKEN_CACHE = {
  tokens: new Map(),

  get(dashboardId) {
    const cached = this.tokens.get(dashboardId);
    if (cached && Date.now() < cached.expiresAt) return cached.token;
    return null;
  },

  get_any(dashboardId) {
    const cached = this.tokens.get(dashboardId);
    return cached?.token || null;
  },

  set(dashboardId, token, expiresInMs = 55 * 60 * 1000) {
    this.tokens.set(dashboardId, {
      token,
      expiresAt: Date.now() + expiresInMs
    });
  },

  clear() {
    this.tokens.clear();
  },

  cleanup() {
    const now = Date.now();
    for (const [dashboardId, data] of this.tokens.entries()) {
      if (now >= data.expiresAt) this.tokens.delete(dashboardId);
    }
  }
};

function safeParseMaybeDoubleEncodedJSON(text) {
  let parsed = JSON.parse(text);
  if (typeof parsed === 'string') parsed = JSON.parse(parsed);
  return parsed;
}

function looks_like_jwt(token) {
  if (typeof token !== 'string') return false;

  const normalizedToken = token.trim();
  if (!normalizedToken) return false;

  const parts = normalizedToken.split('.');
  return parts.length === 3 && parts.every(part => part.length > 0);
}

function extract_guest_token(payload) {
  const candidateToken = payload?.data?.token
    || payload?.data?.guest_token
    || payload?.token
    || payload?.guest_token
    || payload?.access_token
    || payload?.data?.access_token
    || null;

  if (!looks_like_jwt(candidateToken)) {
    return null;
  }

  return candidateToken.trim();
}

function normalize_menu_value(value) {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

  if (normalized === 'programme') return 'programmes';

  return normalized;
}

export default {
  name: 'DashboardView',

  components: {
    HeaderSection,
    PortfolioSuperSetChart,
    PortfolioSidebar,
    BolideAssistant,
    HelpSupport,
    ProgrammeComponent,
    SettingsComponent,
    VerificationComponent,
    IntegrationComponent,
    BolideLoader,
    CompanyProfile,
    CertificatesAndReports
  },

  data() {
    return ({
      active_menu_item: 'portfolio-comparisons',
      tiles: [],
      breadcrumb: [],
      selected_programme_slug: null,
      selected_programme_name: null,
      iframeLoading: false,
      iframeError: false,
      authToken: null,
      embeddedDashboards: new Set(),
      tokenRefreshInProgress: false,
      warned_stale_dashboard_ids: new Set(),
      showAssistant: false,
      sidebar_collapsed: false,
      min_loader_time: 3500,
      loader_start_time: null,
      is_loading: true,
      is_mobile: window.innerWidth < 768,
      mobile_sidebar_open: false,
      sdkLoaded: false,
      sdkConfigured: false,
      initializationComplete: false
    });
  },

  computed: {
    current_dashboard_id() {
      if (this.is_special_view) {
        return null;
      }

      return global_comparisons_dashboard_id;
    },

    is_integration_view() {
      return normalize_menu_value(this.active_menu_item) === 'integration';
    },

    is_help_view() {
      return normalize_menu_value(this.active_menu_item) === 'help';
    },

    is_settings_view() {
      return normalize_menu_value(this.active_menu_item) === 'settings';
    },

    is_programme_view() {
      return normalize_menu_value(this.active_menu_item) === 'programmes';
    },

    is_verification_view() {
      return normalize_menu_value(this.active_menu_item) === 'verification';
    },

    is_company_profile_view() {
      return normalize_menu_value(this.active_menu_item) === 'company profile';
    },

    is_certificates_reports_view() {
      return normalize_menu_value(this.active_menu_item) === 'certificates reports';
    },

    is_special_view() {
      return (
        this.is_help_view ||
        this.is_settings_view ||
        this.is_verification_view ||
        this.is_integration_view ||
        this.is_company_profile_view ||
        this.is_programme_view ||
        this.is_certificates_reports_view
      );
    }
  },

  mounted() {
    window.addEventListener('resize', this.handle_resize);
    window.addEventListener('message', this.handleDashboardMessage);

    this.is_loading = true;
    this.loader_start_time = Date.now();
    this.tiles = getTilesConfiguration().map(tile => ({
      ...tile,
      is_live: String(tile.id || '').includes('(Live)')
    }));
    this.breadcrumb = [{ id: 'main', title: 'Dashboard' }];

    this.$nextTick(() => {
      this.initializeDashboard();
    });

    setTimeout(() => {
      if (this.is_loading) {
        this.is_loading = false;
      }
    }, 10000);

    this.tokenCleanupInterval = setInterval(() => {
      TOKEN_CACHE.cleanup();
    }, 60000);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.handle_resize);
    window.removeEventListener('message', this.handleDashboardMessage);
    this.cleanupDashboards();
    if (this.tokenCleanupInterval) clearInterval(this.tokenCleanupInterval);
  },

  methods: {
    toggleSidebar() {
      this.sidebar_collapsed = !this.sidebar_collapsed;
    },

    handle_resize() {
      this.is_mobile = window.innerWidth < 768;
    },

    toggle_mobile_sidebar() {
      this.mobile_sidebar_open = !this.mobile_sidebar_open;
    },

    close_sidebar() {
      this.mobile_sidebar_open = false;
    },

    handleDashboardMessage(event) {
      if (event.data?.type === 'dashboardLoadComplete' || event.data?.event === 'dashboard_loaded') {
        if (this.is_loading) this.is_loading = false;
      }
    },

    async initializeDashboard() {
      try {
        if (this.is_special_view) {
          this.is_loading = false;
          this.iframeLoading = false;
          return;
        }

        this.iframeLoading = true;
        this.iframeError = false;
        this.authToken = get_token();

        if (!this.authToken) throw new Error('No authentication token found.');

        await this.initializeSupersetSDK();
        await this.configureSupersetSDK();
        await this.embedDashboard('dashboard-main', this.current_dashboard_id);

        const elapsed = Date.now() - this.loader_start_time;

        if (elapsed >= this.min_loader_time) {
          this.is_loading = false;
        } else {
          setTimeout(() => {
            this.is_loading = false;
          }, this.min_loader_time - elapsed);
        }

        this.initializationComplete = true;
      } catch (error) {
        console.error('Dashboard initialization failed:', error);
        this.iframeError = true;
        this.iframeLoading = false;
      }
    },

    async fetchGuestToken(dashboardId = null) {
      if (!dashboardId) {
        return { token: null };
      }

      if (this.tokenRefreshInProgress) {
        await new Promise(resolve => {
          const checkInterval = setInterval(() => {
            if (!this.tokenRefreshInProgress) {
              clearInterval(checkInterval);
              resolve();
            }
          }, 100);
        });

        const cachedToken = TOKEN_CACHE.get(dashboardId);
        if (cachedToken) return { token: cachedToken };
      }

      this.tokenRefreshInProgress = true;

      try {
        const targetDashboardId = dashboardId || SUPERSET_CONFIG.defaultDashboardIds.main;
        const cachedToken = TOKEN_CACHE.get(targetDashboardId);
        if (cachedToken) return { token: cachedToken };

        const response = await fetch(AUTH_CONFIG.guestTokenUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Token ${this.authToken}`,
            Accept: 'application/json'
          },
          body: JSON.stringify({ dashboard_id: targetDashboardId, client_app: 'npi' })
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Guest token request failed: ${response.status}, ${errorText}`);
        }

        const data = safeParseMaybeDoubleEncodedJSON(await response.text());
        const guestToken = extract_guest_token(data);

        if (data.status === 'success' && guestToken) {
          TOKEN_CACHE.set(targetDashboardId, guestToken);
          return ({
            token: guestToken
          });
        }

        throw new Error(data.message || 'Guest token response did not contain a valid JWT');
      } catch (error) {
        const staleToken = TOKEN_CACHE.get_any(dashboardId);
        if (looks_like_jwt(staleToken)) {
          console.warn('Using stale guest token because refresh failed:', error);
          return ({
            token: staleToken
          });
        }

        throw new Error(`Failed to fetch guest token: ${error.message}`);
      } finally {
        this.tokenRefreshInProgress = false;
      }
    },

    async fetch_guest_token() {
      const currentDashboardId = this.current_dashboard_id;

      if (!currentDashboardId) {
        return '';
      }

      try {
        const tokenData = await this.fetchGuestToken(currentDashboardId);
        if (!tokenData || !looks_like_jwt(tokenData.token)) {
          throw new Error('Invalid or missing guest token received');
        }
        return tokenData.token;
      } catch (error) {
        const staleCurrentToken = TOKEN_CACHE.get_any(currentDashboardId);
        if (looks_like_jwt(staleCurrentToken)) {
          return staleCurrentToken;
        }

        if (currentDashboardId !== SUPERSET_CONFIG.defaultDashboardIds.main) {
          try {
            const fallbackTokenData = await this.fetchGuestToken(SUPERSET_CONFIG.defaultDashboardIds.main);
            if (fallbackTokenData && looks_like_jwt(fallbackTokenData.token)) {
              return fallbackTokenData.token;
            }
          } catch (fallbackError) {
            console.error('Fallback guest token fetch failed:', fallbackError);
          }
        }

        return '';
      }
    },

    async initializeSupersetSDK() {
      this.sdkLoaded = true;
      return true;
    },

    async configureSupersetSDK() {
      this.sdkConfigured = true;
    },

    async embedDashboard(containerId, dashboardId, forceRefreshToken = false) {
      if (!dashboardId) {
        this.iframeLoading = false;
        return;
      }

      try {
        this.iframeLoading = true;
        this.iframeError = false;

        const mountPoint = document.getElementById(containerId);
        if (!mountPoint) throw new Error(`Mount point not found: ${containerId}`);

        mountPoint.innerHTML = '';

        if (forceRefreshToken) {
          TOKEN_CACHE.tokens.delete(dashboardId);
        }

        const initialTokenData = await this.fetchGuestToken(dashboardId);
        if (!initialTokenData || !looks_like_jwt(initialTokenData.token)) {
          throw new Error('Superset guest token is invalid');
        }

        await this.initializeSupersetSDK();
        await this.configureSupersetSDK();

        await embedDashboard({
          id: dashboardId,
          supersetDomain: SUPERSET_CONFIG.baseUrl,
          mountPoint,
          fetchGuestToken: async () => {
            const token = await this.fetch_guest_token();

            if (!looks_like_jwt(token)) {
              const staleToken = TOKEN_CACHE.get_any(dashboardId);
              if (looks_like_jwt(staleToken)) {
                if (!this.warned_stale_dashboard_ids.has(dashboardId)) {
                  console.warn('Superset refresh returned an invalid JWT; reusing stale token');
                  this.warned_stale_dashboard_ids.add(dashboardId);
                }
                return staleToken;
              }

              this.iframeError = true;
              this.iframeLoading = false;
              return initialTokenData.token;
            }

            return token;
          },
          dashboardUiConfig: {
            hideTitle: true,
            hideTab: true,
            filters: { visible: true, expanded: true }
          }
        });

        this.iframeLoading = false;
        this.embeddedDashboards.add(containerId);
      } catch (error) {
        console.error('Failed to embed dashboard:', error);
        this.iframeLoading = false;
        this.iframeError = true;
      }
    },

    cleanupDashboards() {
      const container = document.getElementById('dashboard-main');
      if (container) container.innerHTML = '';
      this.embeddedDashboards.clear();
    },

    handleBreadcrumbClick(breadcrumbId) {
      if (breadcrumbId === 'main') {
        this.goBack();
      }
    },

    async goBack() {
      this.active_menu_item = 'portfolio-comparisons';
      this.selected_programme_slug = null;
      this.selected_programme_name = null;
      this.breadcrumb = [{ id: 'main', title: 'Dashboard' }];
      this.cleanupDashboards();
      TOKEN_CACHE.clear();

      this.$nextTick(async () => {
        try {
          await this.embedDashboard('dashboard-main', this.current_dashboard_id, true);
        } catch (error) {
          console.error('Failed to load main dashboard:', error);
        }
      });
    },

    handleProgrammeOpen(programme) {
      this.selected_programme_slug = programme?.slug || null;
      this.selected_programme_name = programme?.name || null;
      this.active_menu_item = 'programmes';
      this.cleanupDashboards();
      this.iframeLoading = false;
      this.iframeError = false;
      this.is_loading = false;
      this.breadcrumb = [{ id: 'programmes', title: this.selected_programme_name || 'Programmes' }];
    },

    async handleGlobalMenuSelected(menuItem) {
      const normalized = normalize_menu_value(menuItem);

      this.active_menu_item = menuItem;

      if (normalized !== 'programmes') {
        this.selected_programme_slug = null;
        this.selected_programme_name = null;
      }

      if (
        normalized !== 'help' &&
        normalized !== 'settings' &&
        normalized !== 'verification' &&
        normalized !== 'integration' &&
        normalized !== 'company profile' &&
        normalized !== 'programmes' &&
        normalized !== 'certificates reports'
      ) {
        this.breadcrumb = [{ id: 'main', title: 'Dashboard' }];

        this.$nextTick(async () => {
          try {
            await this.embedDashboard('dashboard-main', this.current_dashboard_id, true);
          } catch (error) {
            console.error('Failed to load dashboard:', error);
          }
        });

        return;
      }

      this.cleanupDashboards();
      this.iframeLoading = false;
      this.iframeError = false;
      this.is_loading = false;

      if (normalized === 'help') {
        this.breadcrumb = [{ id: 'help', title: 'Help & Support' }];
      } else if (normalized === 'settings') {
        this.breadcrumb = [{ id: 'settings', title: 'Settings' }];
      } else if (normalized === 'verification') {
        this.breadcrumb = [{ id: 'verification', title: 'Verification' }];
      } else if (normalized === 'integration') {
        this.breadcrumb = [{ id: 'integration', title: 'Integration' }];
      } else if (normalized === 'programmes') {
        this.breadcrumb = [{ id: 'programmes', title: 'Programmes' }];
      } else if (normalized === 'certificates reports') {
        this.breadcrumb = [{ id: 'certificates reports', title: 'Certificates & Reports' }];
      } else {
        this.breadcrumb = [{ id: 'company profile', title: 'Company Profile' }];
      }
    }
  }
};
